import React, { useState } from 'react';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import FormCheck from 'react-bootstrap/FormCheck';
import Modal from 'react-bootstrap/Modal';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Spinner from 'react-bootstrap/Spinner';
import Stack from 'react-bootstrap/Stack';
import Table from 'react-bootstrap/Table';
import Tooltip from 'react-bootstrap/Tooltip';
import { QuestionCircle } from 'react-bootstrap-icons';
import { useChargeAllMutation, useStageInvoicesMutation } from '../../mutations/payment.mutation';
import { useChargeableInvoicesQuery } from '../../queries/payment.query';

function ChargeActionsPanel() {
  const [showModal, setShowModal] = useState(false);
  const [sendEmails, setSendEmails] = useState(true);
  const [noPriorAttempts, setNoPriorAttempts] = useState(false);

  const chargeAllMutation = useChargeAllMutation();
  const stageInvoicesMutation = useStageInvoicesMutation();

  const chargeableInvoicesQuery = useChargeableInvoicesQuery(
    { onlyNoPriorAttempts: noPriorAttempts },
    true
  );

  const handleOpenModal = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleChargeAll = () => {
    chargeAllMutation.mutate(
      {
        sendDelinquentEmail: sendEmails,
        onlyChargeNoPriorAttempts: noPriorAttempts
      },
      {
        onSuccess: () => {
          handleCloseModal();
          chargeableInvoicesQuery.refetch();
        }
      }
    );
  };

  const handleStageInvoices = () => {
    stageInvoicesMutation.mutate();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const invoiceCount = chargeableInvoicesQuery.data?.totalCount ?? 0;
  const totalAmount = chargeableInvoicesQuery.data?.totalAmount ?? 0;

  const sendEmailsTooltip = (
    <Tooltip id="send-emails-tooltip">
      When enabled, customers with declined cards or no card on file will receive an email
      notification requesting updated payment information.
    </Tooltip>
  );

  const noPriorAttemptsTooltip = (
    <Tooltip id="no-prior-attempts-tooltip">
      When enabled, only invoices that have never been charged before will be processed. Invoices
      with any previous charge attempts (successful or failed) will be skipped.
    </Tooltip>
  );

  return (
    <>
      <Card className="mb-4">
        <Card.Body>
          <Stack direction="horizontal" gap={3} className="flex-wrap">
            <Button
              variant="outline-primary"
              onClick={handleStageInvoices}
              disabled={stageInvoicesMutation.isLoading}>
              {stageInvoicesMutation.isLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Staging...
                </>
              ) : (
                'Stage Invoices'
              )}
            </Button>

            <Button
              variant="primary"
              onClick={handleOpenModal}
              disabled={chargeableInvoicesQuery.isLoading}>
              {chargeableInvoicesQuery.isLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading...
                </>
              ) : (
                <>
                  {invoiceCount} Invoice{invoiceCount !== 1 ? 's' : ''} to Charge
                  {invoiceCount > 0 && ` (${formatCurrency(totalAmount)})`}
                </>
              )}
            </Button>

            <div className="vr d-none d-md-block" />

            <Stack direction="horizontal" gap={2}>
              <FormCheck
                type="checkbox"
                id="no-prior-attempts-check"
                label="No Prior Attempts"
                checked={noPriorAttempts}
                onChange={(e) => setNoPriorAttempts(e.target.checked)}
              />
              <OverlayTrigger placement="top" overlay={noPriorAttemptsTooltip}>
                <QuestionCircle className="text-muted" style={{ cursor: 'help' }} />
              </OverlayTrigger>
            </Stack>
          </Stack>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={handleCloseModal} size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>
            Invoices to Charge ({invoiceCount}) - {formatCurrency(totalAmount)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {chargeableInvoicesQuery.isLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-2">Loading invoices...</p>
            </div>
          ) : chargeableInvoicesQuery.data?.invoices.length === 0 ? (
            <p className="text-center text-muted py-4">No invoices to charge</p>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Amount</th>
                  <th>Invoice Date</th>
                  <th>Failed Attempts</th>
                </tr>
              </thead>
              <tbody>
                {chargeableInvoicesQuery.data?.invoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td>{invoice.clientName}</td>
                    <td>{formatCurrency(invoice.amount)}</td>
                    <td>{formatDate(invoice.invoiceDate)}</td>
                    <td>{invoice.failedAttemptCount}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Stack direction="horizontal" gap={3} className="w-100 justify-content-between flex-wrap">
            <Stack direction="horizontal" gap={3}>
              <Stack direction="horizontal" gap={2}>
                <FormCheck
                  type="checkbox"
                  id="modal-send-emails-check"
                  label="Send Emails"
                  checked={sendEmails}
                  onChange={(e) => setSendEmails(e.target.checked)}
                />
                <OverlayTrigger placement="top" overlay={sendEmailsTooltip}>
                  <QuestionCircle className="text-muted" style={{ cursor: 'help' }} />
                </OverlayTrigger>
              </Stack>
            </Stack>
            <Stack direction="horizontal" gap={2}>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleChargeAll}
                disabled={chargeAllMutation.isLoading || invoiceCount === 0}>
                {chargeAllMutation.isLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Charging...
                  </>
                ) : (
                  `Charge All (${formatCurrency(totalAmount)})`
                )}
              </Button>
            </Stack>
          </Stack>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default ChargeActionsPanel;
